import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  TextField,
  IconButton,
  Button,
  Tabs,
  Tab,
  Stack,
  Tooltip,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import PaymentsIcon from '@mui/icons-material/Payments';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GroupIcon from '@mui/icons-material/Group';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { getProjectCrew } from '../services/crewService';
import { getBudget } from '../services/budgetService';
import payrollSettingsService from '../services/payrollSettingsService';
import payrollService from '../services/payrollService';

// Simple CSV helper
const downloadCSV = (rows, filename) => {
  const header = Object.keys(rows[0] || {}).join(',');
  const body = rows
    .map((r) => Object.values(r || {}).map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const csv = [header, body].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const defaultHours = {
  dayType: 'shoot',
  baseHours: 0,
  otHours: 0,
  dtHours: 0,
  kit: 0,
  perDiem: 0,
  mealPenalty: 0,
  mileage: 0,
  turnaroundPenalty: 0,
  callTime: '',
  meal1: '',
  meal2: '',
  wrapTime: '',
  holiday: false,
  sixthDay: false,
  seventhDay: false,
  notes: ''
};

const roundMinutesToTenthsHours = (totalMinutes) => {
  const roundedMinutes = Math.round(totalMinutes / 6) * 6; // 6 minutes = 0.1 hour
  return Number((roundedMinutes / 60).toFixed(1));
};

const parseTimeToMinutes = (value) => {
  if (!value || typeof value !== 'string') return NaN;
  const [h, m] = value.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return NaN;
  return h * 60 + m;
};

const computeBaseFromTimes = (row = {}) => {
  const start = parseTimeToMinutes(row.callTime);
  const end = parseTimeToMinutes(row.wrapTime);
  if (Number.isNaN(start) || Number.isNaN(end)) return null;
  let duration = end - start;
  if (duration < 0) {
    // handle overnight
    duration += 24 * 60;
  }
  let breaks = 0;
  if (row.meal1) breaks += 30;
  if (row.meal2) breaks += 30;
  const minutes = Math.max(0, duration - breaks);
  return roundMinutesToTenthsHours(minutes);
};

const computeUnionHours = (row = {}) => {
  const start = parseTimeToMinutes(row.callTime);
  const end = parseTimeToMinutes(row.wrapTime);
  if (Number.isNaN(start) || Number.isNaN(end)) return null;
  let duration = end - start;
  if (duration < 0) duration += 24 * 60; // overnight
  let breaks = 0;
  if (row.meal1) breaks += 30;
  if (row.meal2) breaks += 30;
  const worked = Math.max(0, duration - breaks);
  const baseMinutes = Math.min(worked, row.baseThresholdMinutes || 8 * 60);
  const otMinutes = Math.min(
    Math.max(worked - (row.baseThresholdMinutes || 8 * 60), 0),
    (row.otThresholdMinutes || 12 * 60) - (row.baseThresholdMinutes || 8 * 60)
  );
  const dtMinutes = Math.max(worked - (row.otThresholdMinutes || 12 * 60), 0);
  return {
    baseHours: roundMinutesToTenthsHours(baseMinutes),
    otHours: roundMinutesToTenthsHours(otMinutes),
    dtHours: roundMinutesToTenthsHours(dtMinutes),
  };
};

const computeMealPenalty = (row = {}) => {
  const call = parseTimeToMinutes(row.callTime);
  const meal = parseTimeToMinutes(row.meal1);
  if (Number.isNaN(call) || Number.isNaN(meal)) return null;
  const scheduled = call + (row.mealTriggerMinutes || 6 * 60);
  let lateMinutes = meal - scheduled;
  if (lateMinutes < 0) return 0;
  const increments = Math.ceil(lateMinutes / (row.mealIncrementMinutes || 30));
  const amount = row.mealPenaltyAmount || 12.5;
  return increments * amount;
};

const getStartOfWeek = (date = new Date()) => {
  const d = new Date(date);
  const day = d.getDay(); // 0-6
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday start
  const monday = new Date(d.setDate(diff));
  monday.setHours(0, 0, 0, 0);
  return monday;
};

const Payroll = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const permissions = usePermissions();
  const can = permissions?.can || (() => false);
  const isAdmin = !!can('manageProject');
  const rateLookupRef = useRef({});
  const budgetItemsRef = useRef([]);
  const [crew, setCrew] = useState([]);
  const [hours, setHours] = useState({});
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [weekStart, setWeekStart] = useState(() => getStartOfWeek().toISOString().slice(0, 10));
  const [saveStatus, setSaveStatus] = useState('');
  // calcMode: manual | nonUnion (base-from-times) | union (8/12 split + meal penalty)
  const [calcMode, setCalcMode] = useState('manual');
  const [presets, setPresets] = useState([]);
  const [savingPreset, setSavingPreset] = useState(false);
  const [presetEdits, setPresetEdits] = useState([]);
  const [savingPresetEdits, setSavingPresetEdits] = useState(false);
  const [presetModalOpen, setPresetModalOpen] = useState(false);
  const [newPreset, setNewPreset] = useState({ key: '', label: '', union: '', unionLocal: '' });

  const getDisplayName = (member) =>
    member?.name ||
    member?.user?.name ||
    member?.user?.fullName ||
    member?.email ||
    member?.user?.email ||
    'Crew';

  const findRate = (role, dept) => {
    const items = budgetItemsRef.current || [];
    const r = (role || '').toLowerCase().trim();
    const d = (dept || '').toLowerCase().trim();
    if (!r) return null;
    // Exact position match
    let hit = items.find((li) => (li.position || '').toLowerCase().trim() === r && li.rate);
    if (hit?.rate) return hit.rate;
    // Description contains role
    hit = items.find((li) => (li.description || '').toLowerCase().includes(r) && li.rate);
    if (hit?.rate) return hit.rate;
    // Department + role combo
    hit = items.find((li) => {
      const pos = (li.position || '').toLowerCase();
      const desc = (li.description || '').toLowerCase();
      return (pos.includes(r) || desc.includes(r)) && (d ? (pos.includes(d) || desc.includes(d)) : true) && li.rate;
    });
    if (hit?.rate) return hit.rate;
    return null;
  };

  const computeDefaultRate = useCallback(
    (person, existingHours = {}) => {
      const pos = (person.role || person.position || '').toLowerCase().trim();
      const lookup = rateLookupRef.current || {};
      const fromLookup = pos && lookup[pos];
      const fromBudget = findRate(person.role || person.position, person.department);
      return (
        person.rate ||
        existingHours.rate ||
        fromLookup ||
        fromBudget ||
        ''
      );
    },
    []
  );

  useEffect(() => {
    setTab(isAdmin ? 0 : 0); // default to Admin for admins, self for crew (self tab index handled below)
  }, [isAdmin]);

  useEffect(() => {
    const loadCrew = async () => {
      if (!projectId) return;
      if (!isAdmin) {
        setCrew([]);
        return;
      }
      try {
        setLoading(true);
        const data = await getProjectCrew(projectId);
        const members = Array.isArray(data)
          ? data
          : Array.isArray(data?.crew)
            ? data.crew
            : Array.isArray(data?.members)
              ? data.members
              : [];
        // Try to hydrate rates from budget line items matching position
        try {
          const budget = await getBudget(projectId);
          const items = Array.isArray(budget?.lineItems)
            ? budget.lineItems
            : Array.isArray(budget?.budget?.lineItems)
              ? budget.budget.lineItems
              : [];
          budgetItemsRef.current = items;
          rateLookupRef.current = items.reduce((acc, item) => {
            if (item.position && item.rate) {
              acc[item.position.toLowerCase().trim()] = item.rate;
            }
            if (item.description && item.rate) {
              acc[item.description.toLowerCase().trim()] = item.rate;
            }
            return acc;
          }, {});
        } catch (e) {
          console.warn('Budget rate preload failed (continuing):', e?.message);
        }

        const hydrated = members.map((m) => {
          const presetKey = getPresetKeyForMember(m) || (m.union ? 'union' : null);
          return {
            ...m,
            presetKey,
            rate: computeDefaultRate(m),
          };
        });
        setCrew(hydrated);
        // Initialize hours state if not present
        setHours((prev) => {
          const next = { ...prev };
          hydrated.forEach((m) => {
            if (!next[m._id]) {
              next[m._id] = { ...defaultHours, rate: m.rate || '', presetKey: m.presetKey };
            } else if (m.presetKey) {
              next[m._id].presetKey = m.presetKey;
            }
          });
          return next;
        });
      } catch (err) {
        console.error('Failed to load crew for payroll:', err);
        setError(err?.message || 'Failed to load crew');
      } finally {
        setLoading(false);
      }
    };
    loadCrew();
  }, [projectId, isAdmin]);

  // Load payroll settings (presets)
  useEffect(() => {
    const loadSettings = async () => {
      if (!projectId) return;
      try {
        const settings = await payrollSettingsService.getPayrollSettings(projectId);
        if (settings) {
          setPresets(settings.presets || []);
          setPresetEdits(settings.presets || []);
          setCalcMode(settings.activePreset || 'manual');
        }
      } catch (e) {
        console.warn('Failed to load payroll settings:', e?.message);
      }
    };
    loadSettings();
  }, [projectId]);

  const currentPreset = useMemo(() => {
    if (!presets || !presets.length) return null;
    return presets.find((p) => p.key === calcMode) || presets[0];
  }, [presets, calcMode]);
  const getPresetByKey = useCallback(
    (key) => (presets || []).find((p) => p.key === key) || currentPreset,
    [presets, currentPreset]
  );
  const getPresetKeyForMember = useCallback(
    (member) => {
      if (!member) return '';
      if (member.presetKey) return member.presetKey;
      if (member.union) {
        const match = (presets || []).find(
          (p) =>
            p.union &&
            p.union.toLowerCase() === member.union.toLowerCase() &&
            (!p.unionLocal || !member.unionLocal || p.unionLocal.toLowerCase() === member.unionLocal.toLowerCase())
        );
        if (match) return match.key;
      }
      return '';
    },
    [presets]
  );

  const handlePresetChange = async (value) => {
    setCalcMode(value);
    if (!projectId || !isAdmin) return;
    try {
      setSavingPreset(true);
      const updated = await payrollSettingsService.updatePayrollSettings(projectId, { activePreset: value });
      if (updated?.presets) setPresets(updated.presets);
    } catch (e) {
      console.warn('Failed to save active preset:', e?.message);
    } finally {
      setSavingPreset(false);
    }
  };

  const openPresetModal = () => {
    setPresetEdits(presets);
    setPresetModalOpen(true);
  };

  const closePresetModal = () => setPresetModalOpen(false);

  const addPreset = () => {
    const key = (newPreset.key || newPreset.union || newPreset.label || '').trim().toLowerCase().replace(/\s+/g, '-');
    if (!key) return;
    if ((presetEdits || []).some((p) => p.key === key)) return;
    const label = newPreset.label?.trim() || key;
    setPresetEdits((prev) => [
      ...(prev || []),
      {
        key,
        label,
        union: newPreset.union || '',
        unionLocal: newPreset.unionLocal || '',
        mealTriggerMinutes: 6 * 60,
        mealIncrementMinutes: 30,
        mealPenaltyAmount: 12.5,
        baseThresholdMinutes: 8 * 60,
        otThresholdMinutes: 12 * 60,
      },
    ]);
    setNewPreset({ key: '', label: '', union: '', unionLocal: '' });
  };

  const handlePresetEditChange = (key, field, value) => {
    setPresetEdits((prev) =>
      (prev || []).map((p) =>
        p.key === key
          ? {
              ...p,
              [field]: value === '' ? '' : Number.isNaN(Number(value)) ? value : Number(value),
            }
          : p
      )
    );
  };

  const savePresetEdits = async () => {
    if (!projectId || !isAdmin) return;
    try {
      setSavingPresetEdits(true);
      const updated = await payrollSettingsService.updatePayrollSettings(projectId, { presets: presetEdits, activePreset: calcMode });
      if (updated?.presets) {
        setPresets(updated.presets);
        setPresetEdits(updated.presets);
      }
      setPresetModalOpen(false);
    } catch (e) {
      console.warn('Failed to save presets:', e?.message);
    } finally {
      setSavingPresetEdits(false);
    }
  };

  // Load timecards
  useEffect(() => {
    const loadTimecards = async () => {
      if (!projectId) return;
      try {
        setLoading(true);
        const weekDate = weekStart ? new Date(weekStart) : undefined;
        const my = await payrollService.getMyTimecard(projectId, { weekStart: weekDate?.toISOString() });
        const cards = isAdmin
          ? await payrollService.getTimecards(projectId, { weekStart: weekDate?.toISOString() })
          : [];

        setHours((prev) => {
          const next = { ...prev };
          if (isAdmin) {
            cards.forEach((c) => {
              const key = c.user || c._id || c.personName;
              if (!key) return;
              const entry = Array.isArray(c.entries) && c.entries[0] ? c.entries[0] : {};
              next[key] = {
                dayType: entry.dayType || 'shoot',
                baseHours: entry.baseHours ?? 0,
                otHours: entry.otHours ?? 0,
                dtHours: entry.dtHours ?? 0,
                kit: entry.kit ?? 0,
                perDiem: entry.perDiem ?? 0,
                notes: entry.notes || '',
                mealPenalty: entry.mealPenalty ?? 0,
                mileage: entry.mileage ?? 0,
                turnaroundPenalty: entry.turnaroundPenalty ?? 0,
                callTime: entry.callTime || '',
                meal1: entry.meal1 || '',
                meal2: entry.meal2 || '',
                wrapTime: entry.wrapTime || '',
                holiday: !!entry.holiday,
                sixthDay: !!entry.sixthDay,
                seventhDay: !!entry.seventhDay,
                rate: c.rate ?? '',
              };
            });
          }
          if (my) {
            const key = my.user || my._id || my.personName || (user?._id || user?.id) || 'self';
            const entry = Array.isArray(my.entries) && my.entries[0] ? my.entries[0] : {};
            const defaultRate = computeDefaultRate(my, hours[key]);
            next[key] = {
              dayType: entry.dayType || 'shoot',
              baseHours: entry.baseHours ?? 0,
              otHours: entry.otHours ?? 0,
              dtHours: entry.dtHours ?? 0,
              kit: entry.kit ?? 0,
              perDiem: entry.perDiem ?? 0,
              mileage: entry.mileage ?? 0,
              turnaroundPenalty: entry.turnaroundPenalty ?? 0,
              callTime: entry.callTime || '',
              meal1: entry.meal1 || '',
              meal2: entry.meal2 || '',
              wrapTime: entry.wrapTime || '',
              holiday: !!entry.holiday,
              sixthDay: !!entry.sixthDay,
              seventhDay: !!entry.seventhDay,
              notes: entry.notes || '',
              mealPenalty: entry.mealPenalty ?? 0,
              rate: my.rate ?? defaultRate ?? '',
              role: my.role,
              department: my.department,
              presetKey: my.presetKey || (my.union ? 'union' : undefined),
            };
            // if no crew info, add minimal record for self display
            if (!isAdmin && crew.length === 0) {
              setCrew([{
                _id: key,
                name: my.personName,
                role: my.role,
                department: my.department,
                rate: my.rate ?? defaultRate ?? ''
              }]);
            }
          }
          return next;
        });
      } catch (err) {
        console.error('Failed to load timecards:', err);
        setError(err?.message || 'Failed to load timecards');
      } finally {
        setLoading(false);
      }
    };
    loadTimecards();
  }, [projectId, weekStart]);

  const handleHoursChange = (id, field, value) => {
    const timeFields = ['callTime', 'meal1', 'meal2', 'wrapTime'];
    setHours((prev) => {
      const current = prev[id] || defaultHours;
      const updated = { ...current, [field]: value };
      const memberPreset = getPresetByKey(current.presetKey);
      const presetForCalc = memberPreset || currentPreset;
      if (timeFields.includes(field)) {
        if (calcMode === 'union') {
          const computed = computeUnionHours({
            ...updated,
            baseThresholdMinutes: presetForCalc?.baseThresholdMinutes,
            otThresholdMinutes: presetForCalc?.otThresholdMinutes,
          });
          if (computed) {
            updated.baseHours = computed.baseHours;
            updated.otHours = computed.otHours;
            updated.dtHours = computed.dtHours;
          }
          const penalty = computeMealPenalty({
            ...updated,
            mealTriggerMinutes: presetForCalc?.mealTriggerMinutes,
            mealIncrementMinutes: presetForCalc?.mealIncrementMinutes,
            mealPenaltyAmount: presetForCalc?.mealPenaltyAmount,
          });
          if (penalty !== null && penalty !== undefined) {
            updated.mealPenalty = penalty;
          }
        } else if (calcMode === 'nonUnion') {
          const computed = computeBaseFromTimes(updated);
          if (computed !== null) {
            updated.baseHours = computed;
          }
          const penalty = computeMealPenalty({
            ...updated,
            mealTriggerMinutes: presetForCalc?.mealTriggerMinutes,
            mealIncrementMinutes: presetForCalc?.mealIncrementMinutes,
            mealPenaltyAmount: presetForCalc?.mealPenaltyAmount,
          });
          if (penalty !== null && penalty !== undefined) {
            updated.mealPenalty = penalty;
          }
        }
      }
      return {
        ...prev,
        [id]: updated,
      };
    });
  };

  const adminRows = useMemo(
    () =>
      crew.map((member) => {
        const row = hours[member._id] || defaultHours;
        const rate = Number(row.rate || 0);
        const base = Number(row.baseHours || 0) * rate;
        const ot = Number(row.otHours || 0) * rate * 1.5;
        const dt = Number(row.dtHours || 0) * rate * 2.0;
        const gross =
          base +
          ot +
          dt +
          Number(row.kit || 0) +
          Number(row.perDiem || 0) +
          Number(row.mealPenalty || 0) +
          Number(row.turnaroundPenalty || 0) +
          Number(row.mileage || 0);
        return { member, row, rate, base, ot, dt, gross };
      }),
    [crew, hours]
  );

  const myCrewRecord = useMemo(
    () => crew.find((m) => (m.user?._id || m.user?.id) === (user?._id || user?.id)),
    [crew, user]
  );

  const myRow = myCrewRecord ? hours[myCrewRecord._id] || defaultHours : null;

  const saveRow = async (memberId, member, row) => {
    if (!projectId) return;
    const weekDate = weekStart ? new Date(weekStart) : getStartOfWeek();
    const payload = {
      user: member.user?._id || member.user?.id || undefined,
      personName: getDisplayName(member),
      role: member.role || '',
      department: member.department || '',
      union: member.union || '',
      unionLocal: member.unionLocal || '',
      presetKey: row.presetKey || member.presetKey || '',
      rate: Number(row.rate || 0),
      weekStart: weekDate.toISOString(),
      entries: [
        {
          date: weekDate.toISOString(),
          baseHours: Number(row.baseHours || 0),
          otHours: Number(row.otHours || 0),
          dtHours: Number(row.dtHours || 0),
          notes: row.notes || '',
          kit: Number(row.kit || 0),
          perDiem: Number(row.perDiem || 0),
          mealPenalty: Number(row.mealPenalty || 0),
          mileage: Number(row.mileage || 0),
          turnaroundPenalty: Number(row.turnaroundPenalty || 0),
          callTime: row.callTime || '',
          meal1: row.meal1 || '',
          meal2: row.meal2 || '',
          wrapTime: row.wrapTime || '',
          holiday: !!row.holiday,
          sixthDay: !!row.sixthDay,
          seventhDay: !!row.seventhDay,
          dayType: row.dayType || 'shoot',
        },
      ],
    };
    await payrollService.upsertTimecard(projectId, payload);
  };

  const saveMyRow = async () => {
    if (!projectId || !myCrewRecord || !myRow) return;
    try {
      setSaveStatus('Saving…');
      await saveRow(myCrewRecord._id, myCrewRecord, myRow);
      setSaveStatus('Saved');
      setTimeout(() => setSaveStatus(''), 2000);
    } catch (e) {
      setSaveStatus(e?.message || 'Save failed');
    }
  };

  const handlePrevWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() - 7);
    setWeekStart(getStartOfWeek(d).toISOString().slice(0, 10));
  };

  const handleNextWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 7);
    setWeekStart(getStartOfWeek(d).toISOString().slice(0, 10));
  };

  const handleExport = () => {
    if (!adminRows.length) return;
    const rows = adminRows.map(({ member, row, rate, base, ot, dt, gross }) => ({
      Name: getDisplayName(member),
      Role: member.role || '',
      Department: member.department || '',
      'Base Hours': row.baseHours || 0,
      'OT Hours': row.otHours || 0,
      'DT Hours': row.dtHours || 0,
      Rate: rate || 0,
      'Gross (est)': gross.toFixed(2),
      Notes: row.notes || '',
    }));
    downloadCSV(rows, 'payroll_timesheet.csv');
  };

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <IconButton onClick={() => navigate(-1)} size="small">
          <ArrowBackIcon />
        </IconButton>
        <PaymentsIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>
          Payroll (Beta)
        </Typography>
        <Chip label="Separate from Budget" size="small" color="default" />
        <Box flexGrow={1} />
        <Stack direction="row" spacing={1} alignItems="center">
          <Button variant="outlined" size="small" onClick={handlePrevWeek}>
            Prev Week
          </Button>
          <TextField
            label="Week Start"
            type="date"
            size="small"
            value={weekStart}
            onChange={(e) => setWeekStart(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& input::-webkit-calendar-picker-indicator': {
                filter: 'invert(1)',
              },
              '& input': {
                color: 'text.primary',
              },
            }}
          />
          <Button variant="outlined" size="small" onClick={handleNextWeek}>
            Next Week
          </Button>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="calc-mode-label">Calculation Mode</InputLabel>
            <Select
              labelId="calc-mode-label"
              value={calcMode}
              label="Calculation Mode"
              onChange={(e) => handlePresetChange(e.target.value)}
            >
              <MenuItem value="manual">Manual entry</MenuItem>
              <MenuItem value="nonUnion">Auto: base + meal penalty</MenuItem>
              <MenuItem value="union">Auto: union (8/12 split + meal penalty)</MenuItem>
            </Select>
          </FormControl>
          {isAdmin && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => handlePresetChange(calcMode)}
              disabled={savingPreset}
            >
              {savingPreset ? 'Saving…' : 'Save preset'}
            </Button>
          )}
          {isAdmin && (
            <Button variant="outlined" size="small" onClick={openPresetModal}>
              Edit presets
            </Button>
          )}
          <Tooltip title="Export CSV for payroll vendor">
            <span>
              <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleExport} disabled={!adminRows.length}>
                Export CSV
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <Dialog open={presetModalOpen} onClose={closePresetModal} fullWidth maxWidth="md">
        <DialogTitle>Edit Calculation Presets</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {presetEdits.map((p) => (
              <Stack key={p.key} direction="row" spacing={2} flexWrap="wrap" rowGap={1} alignItems="center">
                <Chip label={p.label || p.key} />
                <TextField
                  label="Meal trigger (min)"
                  type="number"
                  size="small"
                  value={p.mealTriggerMinutes ?? ''}
                  onChange={(e) => handlePresetEditChange(p.key, 'mealTriggerMinutes', e.target.value)}
                  inputProps={{ min: 0, step: 30 }}
                />
                <TextField
                  label="Meal increment (min)"
                  type="number"
                  size="small"
                  value={p.mealIncrementMinutes ?? ''}
                  onChange={(e) => handlePresetEditChange(p.key, 'mealIncrementMinutes', e.target.value)}
                  inputProps={{ min: 0, step: 15 }}
                />
                <TextField
                  label="Meal penalty ($)"
                  type="number"
                  size="small"
                  value={p.mealPenaltyAmount ?? ''}
                  onChange={(e) => handlePresetEditChange(p.key, 'mealPenaltyAmount', e.target.value)}
                  inputProps={{ min: 0, step: 1 }}
                />
                <TextField
                  label="Base threshold (min)"
                  type="number"
                  size="small"
                  value={p.baseThresholdMinutes ?? ''}
                  onChange={(e) => handlePresetEditChange(p.key, 'baseThresholdMinutes', e.target.value)}
                  inputProps={{ min: 0, step: 30 }}
                />
                <TextField
                  label="OT->DT threshold (min)"
                  type="number"
                  size="small"
                  value={p.otThresholdMinutes ?? ''}
                  onChange={(e) => handlePresetEditChange(p.key, 'otThresholdMinutes', e.target.value)}
                  inputProps={{ min: 0, step: 30 }}
                />
              </Stack>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePresetModal}>Cancel</Button>
          <Button onClick={savePresetEdits} variant="contained" disabled={savingPresetEdits}>
            {savingPresetEdits ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile>
        {isAdmin && <Tab icon={<GroupIcon />} iconPosition="start" label="Admin View" value={0} />}
        <Tab icon={<AccessTimeIcon />} iconPosition="start" label="My Timesheet" disabled={!myRow} value={isAdmin ? 1 : 0} />
      </Tabs>

      {isAdmin && tab === 0 && (
        <Card>
          <CardHeader
            title="Crew Hours"
            subheader={
              error
                ? error
                : loading
                  ? 'Loading crew...'
                  : 'Enter base/OT/DT hours. CSV export maps to common payroll templates.'
            }
          />
          <CardContent>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Dept</TableCell>
                    <TableCell align="center">Day</TableCell>
                    <TableCell align="right">Rate</TableCell>
                    <TableCell align="center">Preset</TableCell>
                    <TableCell align="right">Base Hrs</TableCell>
                    <TableCell align="right">OT Hrs</TableCell>
                    <TableCell align="right">DT Hrs</TableCell>
                    <TableCell align="right">Kit</TableCell>
                    <TableCell align="right">Per Diem</TableCell>
                    <TableCell align="right">Meal Penalty</TableCell>
                    <TableCell align="right">Mileage</TableCell>
                    <TableCell align="right">Turnaround</TableCell>
                    <TableCell>Call / Meal1 / Meal2 / Wrap</TableCell>
                    <TableCell align="center">Hol</TableCell>
                    <TableCell align="center">6th</TableCell>
                    <TableCell align="center">7th</TableCell>
                    <TableCell align="right">Gross (est)</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {adminRows.map(({ member, row, rate, gross }) => (
                    <TableRow key={member._id}>
                      <TableCell>{getDisplayName(member)}</TableCell>
                      <TableCell>{member.role || '—'}</TableCell>
                      <TableCell>{member.department || '—'}</TableCell>
                      <TableCell align="center">
                        <TextField
                          select
                          size="small"
                          value={row.dayType || 'shoot'}
                          onChange={(e) => handleHoursChange(member._id, 'dayType', e.target.value)}
                          SelectProps={{ native: true }}
                          sx={{ minWidth: 100 }}
                        >
                          <option value="prep">Prep</option>
                          <option value="shoot">Shoot</option>
                          <option value="wrap">Wrap</option>
                          <option value="travel">Travel</option>
                        </TextField>
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          type="number"
                          size="small"
                          value={row.rate ?? member.rate ?? ''}
                          onChange={(e) => handleHoursChange(member._id, 'rate', e.target.value)}
                          inputProps={{ min: 0, step: 1, style: { textAlign: 'right', width: 80 } }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          select
                          size="small"
                          value={row.presetKey || member.presetKey || calcMode}
                          onChange={(e) => handleHoursChange(member._id, 'presetKey', e.target.value)}
                          SelectProps={{ native: true }}
                          sx={{ minWidth: 140 }}
                        >
                          {presets.map((p) => (
                            <option key={p.key} value={p.key}>
                              {p.label}
                            </option>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          type="number"
                          size="small"
                          value={row.baseHours ?? ''}
                          onChange={(e) => handleHoursChange(member._id, 'baseHours', e.target.value)}
                          inputProps={{ min: 0, step: 0.5, style: { textAlign: 'right', width: 80 } }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          type="number"
                          size="small"
                          value={row.otHours ?? ''}
                          onChange={(e) => handleHoursChange(member._id, 'otHours', e.target.value)}
                          inputProps={{ min: 0, step: 0.5, style: { textAlign: 'right', width: 80 } }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          type="number"
                          size="small"
                          value={row.dtHours ?? ''}
                          onChange={(e) => handleHoursChange(member._id, 'dtHours', e.target.value)}
                          inputProps={{ min: 0, step: 0.5, style: { textAlign: 'right', width: 80 } }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          type="number"
                          size="small"
                          value={row.kit ?? ''}
                          onChange={(e) => handleHoursChange(member._id, 'kit', e.target.value)}
                          inputProps={{ min: 0, step: 1, style: { textAlign: 'right', width: 80 } }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          type="number"
                          size="small"
                          value={row.perDiem ?? ''}
                          onChange={(e) => handleHoursChange(member._id, 'perDiem', e.target.value)}
                          inputProps={{ min: 0, step: 1, style: { textAlign: 'right', width: 80 } }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={600}>${gross.toFixed(2)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          type="number"
                          size="small"
                          value={row.mealPenalty ?? ''}
                          onChange={(e) => handleHoursChange(member._id, 'mealPenalty', e.target.value)}
                          inputProps={{ min: 0, step: 1, style: { textAlign: 'right', width: 80 } }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          type="number"
                          size="small"
                          value={row.mileage ?? ''}
                          onChange={(e) => handleHoursChange(member._id, 'mileage', e.target.value)}
                          inputProps={{ min: 0, step: 1, style: { textAlign: 'right', width: 80 } }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          type="number"
                          size="small"
                          value={row.turnaroundPenalty ?? ''}
                          onChange={(e) => handleHoursChange(member._id, 'turnaroundPenalty', e.target.value)}
                          inputProps={{ min: 0, step: 1, style: { textAlign: 'right', width: 80 } }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <TextField
                            label="Call"
                            type="time"
                            size="small"
                            value={row.callTime || ''}
                            onChange={(e) => handleHoursChange(member._id, 'callTime', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            label="Meal 1"
                            type="time"
                            size="small"
                            value={row.meal1 || ''}
                            onChange={(e) => handleHoursChange(member._id, 'meal1', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            label="Meal 2"
                            type="time"
                            size="small"
                            value={row.meal2 || ''}
                            onChange={(e) => handleHoursChange(member._id, 'meal2', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            label="Wrap"
                            type="time"
                            size="small"
                            value={row.wrapTime || ''}
                            onChange={(e) => handleHoursChange(member._id, 'wrapTime', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant={row.holiday ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => handleHoursChange(member._id, 'holiday', !row.holiday)}
                        >
                          Hol
                        </Button>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant={row.sixthDay ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => handleHoursChange(member._id, 'sixthDay', !row.sixthDay)}
                        >
                          6th
                        </Button>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant={row.seventhDay ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => handleHoursChange(member._id, 'seventhDay', !row.seventhDay)}
                        >
                          7th
                        </Button>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={600}>${gross.toFixed(2)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <TextField
                            size="small"
                            value={row.notes ?? ''}
                            onChange={(e) => handleHoursChange(member._id, 'notes', e.target.value)}
                            placeholder="Notes"
                          />
                          <Button size="small" variant="outlined" onClick={() => saveRow(member._id, member, row)}>
                            Save
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!adminRows.length && (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No crew found. Add crew members first.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {tab === 1 && myRow && (
        <Card>
          <CardHeader
            title="My Timesheet"
            subheader={`Enter your hours; this view is limited to your record. Week of ${weekStart}`}
          />
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="subtitle1" fontWeight={700}>
                {getDisplayName(myCrewRecord)}{' '}
                <Typography component="span" variant="subtitle2" color="text.secondary">
                  {myCrewRecord?.role || 'Crew'} {myCrewRecord?.department ? `• ${myCrewRecord.department}` : ''}
                </Typography>
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={1}>
                <TextField
                  label="Day Type"
                  select
                  size="small"
                  value={myRow.dayType || 'shoot'}
                  onChange={(e) => handleHoursChange(myCrewRecord._id, 'dayType', e.target.value)}
                  SelectProps={{ native: true }}
                >
                  <option value="prep">Prep</option>
                  <option value="shoot">Shoot</option>
                  <option value="wrap">Wrap</option>
                  <option value="travel">Travel</option>
                </TextField>
                <TextField
                  label="Rate"
                  type="number"
                  size="small"
                  value={myRow.rate ?? ''}
                  onChange={(e) => handleHoursChange(myCrewRecord._id, 'rate', e.target.value)}
                  inputProps={{ min: 0, step: 1 }}
                />
                <TextField
                  label="Base Hours"
                  type="number"
                  size="small"
                  value={myRow.baseHours ?? ''}
                  onChange={(e) => handleHoursChange(myCrewRecord._id, 'baseHours', e.target.value)}
                  inputProps={{ min: 0, step: 0.5 }}
                />
                <TextField
                  label="OT Hours"
                  type="number"
                  size="small"
                  value={myRow.otHours ?? ''}
                  onChange={(e) => handleHoursChange(myCrewRecord._id, 'otHours', e.target.value)}
                  inputProps={{ min: 0, step: 0.5 }}
                />
                <TextField
                  label="DT Hours"
                  type="number"
                  size="small"
                  value={myRow.dtHours ?? ''}
                  onChange={(e) => handleHoursChange(myCrewRecord._id, 'dtHours', e.target.value)}
                  inputProps={{ min: 0, step: 0.5 }}
                />
                <TextField
                  label="Kit"
                  type="number"
                  size="small"
                  value={myRow.kit ?? ''}
                  onChange={(e) => handleHoursChange(myCrewRecord._id, 'kit', e.target.value)}
                  inputProps={{ min: 0, step: 1 }}
                />
                <TextField
                  label="Per Diem"
                  type="number"
                  size="small"
                  value={myRow.perDiem ?? ''}
                  onChange={(e) => handleHoursChange(myCrewRecord._id, 'perDiem', e.target.value)}
                  inputProps={{ min: 0, step: 1 }}
                />
                <TextField
                  label="Meal Penalty"
                  type="number"
                  size="small"
                  value={myRow.mealPenalty ?? ''}
                  onChange={(e) => handleHoursChange(myCrewRecord._id, 'mealPenalty', e.target.value)}
                  inputProps={{ min: 0, step: 1 }}
                />
              </Stack>
              <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={1}>
                <TextField
                  label="Mileage"
                  type="number"
                  size="small"
                  value={myRow.mileage ?? ''}
                  onChange={(e) => handleHoursChange(myCrewRecord._id, 'mileage', e.target.value)}
                  inputProps={{ min: 0, step: 1 }}
                />
                <TextField
                  label="Turnaround Penalty"
                  type="number"
                  size="small"
                  value={myRow.turnaroundPenalty ?? ''}
                  onChange={(e) => handleHoursChange(myCrewRecord._id, 'turnaroundPenalty', e.target.value)}
                  inputProps={{ min: 0, step: 1 }}
                />
              </Stack>
              <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={1}>
                <TextField
                  label="Call"
                  type="time"
                  size="small"
                  value={myRow.callTime || ''}
                  onChange={(e) => handleHoursChange(myCrewRecord._id, 'callTime', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Meal 1"
                  type="time"
                  size="small"
                  value={myRow.meal1 || ''}
                  onChange={(e) => handleHoursChange(myCrewRecord._id, 'meal1', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Meal 2"
                  type="time"
                  size="small"
                  value={myRow.meal2 || ''}
                  onChange={(e) => handleHoursChange(myCrewRecord._id, 'meal2', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Wrap"
                  type="time"
                  size="small"
                  value={myRow.wrapTime || ''}
                  onChange={(e) => handleHoursChange(myCrewRecord._id, 'wrapTime', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button
                  variant={myRow.holiday ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => handleHoursChange(myCrewRecord._id, 'holiday', !myRow.holiday)}
                >
                  Holiday
                </Button>
                <Button
                  variant={myRow.sixthDay ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => handleHoursChange(myCrewRecord._id, 'sixthDay', !myRow.sixthDay)}
                >
                  6th
                </Button>
                <Button
                  variant={myRow.seventhDay ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => handleHoursChange(myCrewRecord._id, 'seventhDay', !myRow.seventhDay)}
                >
                  7th
                </Button>
              </Stack>
              <TextField
                label="Notes"
                size="small"
                value={myRow.notes ?? ''}
                onChange={(e) => handleHoursChange(myCrewRecord._id, 'notes', e.target.value)}
                fullWidth
              />
              <Box>
                <Button variant="contained" onClick={saveMyRow}>
                  Save My Timesheet
                </Button>
                {saveStatus && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    {saveStatus}
                  </Typography>
                )}
              </Box>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              This is a lightweight self-entry view; approval/export happens in Admin View.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default Payroll;
